import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Plus, X, Sparkle, MapPin, Lightbulb, MagnifyingGlass,
    LockKey, CheckCircle,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { RichText } from "../components/RichText";

function ProblemRow({ p, expanded, onToggle }) {
    return (
        <div
            data-testid={`problem-row-${p.id}`}
            className="border-b border-border"
        >
            <button
                onClick={onToggle}
                className="w-full grid grid-cols-[1fr_auto_auto] items-center gap-4 px-2 sm:px-4 py-5 text-left hover:bg-muted/40 transition-colors"
                data-testid={`problem-toggle-${p.id}`}
            >
                <div className="min-w-0">
                    <div className="text-foreground text-base sm:text-lg leading-snug truncate">
                        {p.title}
                    </div>
                </div>
                <div className="text-muted-foreground text-xs sm:text-sm font-medium hidden sm:block">
                    {p.category}
                </div>
                <div
                    className={`h-7 w-7 rounded-full grid place-items-center border transition-colors
                        ${expanded
                            ? "border-[#6C63FF] text-[#6C63FF] bg-[#6C63FF]/10"
                            : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}
                >
                    {expanded ? <X size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                </div>
            </button>
            {expanded && (
                <div className="px-2 sm:px-4 pb-6 fade-in" data-testid={`problem-expanded-${p.id}`}>
                    <div className="rounded-xl bg-card border border-border p-6 sm:p-8">
                        <p className="text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-wrap">
                            <RichText text={p.description} />
                        </p>
                        {p.image_url && (
                            <div className="mt-6 rounded-lg overflow-hidden bg-muted/30 border border-border flex items-center justify-center">
                                <img
                                    src={p.image_url}
                                    alt={p.title}
                                    className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
                                    data-testid={`problem-image-${p.id}`}
                                />
                            </div>
                        )}
                        {p.contact_email && (
                            <div className="mt-6 pt-5 border-t border-border text-xs text-muted-foreground">
                                Contact: <a className="text-[#6C63FF] font-semibold" href={`mailto:${p.contact_email}`}>{p.contact_email}</a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [posts, setPosts] = useState([]);
    // Home page defaults to the Sample category — always free, always
    // visible — so a freshly-logged-in user lands on real content
    // instead of an empty "All" wall or a paywall.
    const [activeCat, setActiveCat] = useState("Sample");
    const [expandedId, setExpandedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [paywallPrice, setPaywallPrice] = useState(null);
    const [paywallCat, setPaywallCat] = useState(null);
    const [paywallBusy, setPaywallBusy] = useState(false);

    // Home is always filtered to the user's home country (params.country
    // = user.country on every request), so a Pro Local user IS unlocked
    // here. Pro Global / admin are unlocked everywhere. Legacy one-time
    // "categories_unlocked" also covers the home country.
    const sub = user?.subscription || "";
    const unlocked =
        user?.role === "admin" ||
        sub === "pro_global" ||
        sub === "pro_local" ||
        // Legacy: a single "pro" value from before Two-tier Pro shipped.
        // Honour it as Pro Global to preserve what the user paid for.
        sub === "pro" ||
        !!user?.categories_unlocked;

    const isFreeCat = (name) => {
        if (!name) return false;
        return name.trim().toLowerCase() === "sample";
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!user || unlocked) return;
        api.get("/payments/unlock-price")
            .then((r) => setPaywallPrice(r.data))
            .catch(() => {});
    }, [user, unlocked]);

    useEffect(() => {
        // HARD RESET: wipe posts to [] before fetching new ones. This makes
        // it impossible for any stale render to leak the previous category's
        // rows into the new selection — even in the face of build-pipeline
        // quirks, double-mount races or CDN chunk mixing in production.
        setPosts([]);
        setExpandedId(null);
        setLoading(true);

        let ignore = false;
        const params = { author_role: "admin", limit: 200, _ts: Date.now() };
        if (user?.country) params.country = user.country;
        if (activeCat !== "all") params.category = activeCat;
        api.get("/posts", { params })
            .then((r) => {
                if (ignore) return;
                let data = Array.isArray(r.data) ? r.data : [];
                // Defensive secondary filter: even if the backend ever
                // returned a row whose `category` didn't match, drop it
                // here. Belt and suspenders.
                if (activeCat !== "all") {
                    data = data.filter((p) => p && p.category === activeCat);
                }
                if (activeCat === "all" && data.length > 1) {
                    // Fisher–Yates shuffle so categories interleave on All.
                    for (let i = data.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [data[i], data[j]] = [data[j], data[i]];
                    }
                }
                setPosts(data);
            })
            .catch(() => {
                // Ignore — the user moved on or network blipped.
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [activeCat, user]);

    const onPillClick = (cat) => {
        // Sample + All are always free.
        if (cat === "all" || isFreeCat(cat) || unlocked) {
            setActiveCat(cat);
            return;
        }
        setPaywallCat(cat);
        setPaywallOpen(true);
    };

    const handleUnlock = async () => {
        setPaywallBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                plan: "pro_monthly",
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch {
            toast.error("Could not start checkout. Try again.");
            setPaywallBusy(false);
        }
    };

    // Compute filtered list INLINE on every render (no useMemo) — useMemo
    // was somehow not recomputing on production after activeCat changes,
    // even though the dependency array included it. Inline is the most
    // bulletproof: filter executes every render, no caching, no closure
    // staleness. The compute cost is trivial (≤ 200 items).
    const _q = search.trim().toLowerCase();
    const filtered = posts.filter((p) => {
        if (activeCat !== "all" && p.category !== activeCat) return false;
        if (!_q) return true;
        return (
            p.title.toLowerCase().includes(_q) ||
            (p.description || "").toLowerCase().includes(_q)
        );
    });

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="home-page">
            <SEO
                title="Problems worth solving"
                description="Curated startup problems and validated ideas from around the world. Discover, discuss, and build the next great thing — by category and country."
                path="/"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 lg:py-12">
                {/* Top action bar */}
                <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-8">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground font-semibold mb-2">
                            {user?.country
                                ? (<span className="inline-flex items-center gap-1"><MapPin size={11} weight="duotone" /> Curated for {user.country}</span>)
                                : "Curated by Ideas Mine"}
                        </div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter brand-text">
                            Problems worth solving.
                        </h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search problems…"
                                data-testid="home-search"
                                className="pl-8 h-10 w-56"
                            />
                        </div>
                        <Button
                            onClick={() => navigate("/ai-chat")}
                            variant="outline"
                            data-testid="cta-ai-chat"
                        >
                            <Sparkle size={16} weight="duotone" className="mr-1" /> AI Assist
                        </Button>
                        <Button
                            onClick={() => navigate("/create")}
                            className="brand-button"
                            data-testid="cta-create-idea"
                        >
                            <Plus size={16} weight="bold" className="mr-1" /> Post idea
                        </Button>
                    </div>
                </header>

                {/* Category pills */}
                <div className="flex flex-wrap gap-2 mb-10" data-testid="category-pills">
                    {categories.map((c) => {
                        const isLocked = !unlocked && !isFreeCat(c.name);
                        const isActive = activeCat === c.name;
                        return (
                            <button
                                key={c.id}
                                onClick={() => onPillClick(c.name)}
                                data-testid={`cat-pill-${c.slug}`}
                                className={`relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors
                                    ${isActive
                                        ? "bg-[#6C63FF] text-white"
                                        : isLocked
                                            ? "bg-muted text-foreground/50 hover:bg-muted/70"
                                            : "bg-muted text-foreground/80 hover:bg-muted/70"}`}
                            >
                                {isLocked && <LockKey size={12} weight="duotone" className="text-[#FF8A00]" />}
                                {c.name}
                            </button>
                        );
                    })}
                </div>

                {/* List header */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-2 sm:px-4 pb-2 border-b border-border">
                    <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground font-semibold">
                        Ideas
                    </div>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground font-semibold hidden sm:block">
                        Category
                    </div>
                    <div className="w-7" />
                </div>

                {/* Rows */}
                {loading ? (
                    <div className="py-6">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="border-b border-border py-5 px-2 sm:px-4">
                                <div className="h-5 w-2/3 bg-muted rounded animate-pulse" />
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center" data-testid="home-empty">
                        <Lightbulb size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                        <h3 className="font-display font-bold text-lg">No problems here yet</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            {search
                                ? "Try a different search term."
                                : `No editor picks for ${user?.country || "your country"} in ${activeCat === "all" ? "any category" : activeCat} yet.`}
                        </p>
                    </div>
                ) : (
                    <div data-testid="problems-list" key={`list-${activeCat}`}>
                        {filtered.map((p) => (
                            <ProblemRow
                                key={`${activeCat}-${p.id}`}
                                p={p}
                                expanded={expandedId === p.id}
                                onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                            />
                        ))}
                    </div>
                )}

                <div className="text-xs text-muted-foreground mt-8">
                    Showing {filtered.length} {filtered.length === 1 ? "problem" : "problems"}
                    {activeCat !== "all" ? ` · ${activeCat}` : ""}
                </div>
            </div>

            {/* Paywall modal */}
            <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
                <DialogContent data-testid="paywall-dialog" className="max-w-md">
                    <DialogHeader>
                        <div className="h-12 w-12 rounded-xl bg-[#FF8A00]/15 text-[#FF8A00] grid place-items-center mb-3">
                            <LockKey size={26} weight="duotone" />
                        </div>
                        <DialogTitle className="font-display font-black text-2xl tracking-tight">
                            Unlock <span className="text-[#6C63FF]">all categories</span>
                        </DialogTitle>
                        <DialogDescription className="leading-relaxed">
                            {paywallCat ? <><b>{paywallCat}</b> and every other category is locked. </> : null}
                            Pay once and instantly browse every admin-curated problem across all 12 categories — forever.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl border border-border bg-card p-5 my-2">
                        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground mb-1">
                            One-time · for {paywallPrice?.country || user?.country || "you"}
                        </div>
                        <div className="font-display font-black text-4xl tracking-tighter">
                            {paywallPrice?.label || "—"}
                        </div>
                        <ul className="mt-4 space-y-1.5 text-sm">
                            <li className="flex items-center gap-2">
                                <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                                All 12 categories unlocked
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                                Country-tailored problem briefs
                            </li>
                            <li className="flex items-center gap-2">
                                <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                                Lifetime access — no renewals
                            </li>
                        </ul>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setPaywallOpen(false)} data-testid="paywall-cancel">
                            Maybe later
                        </Button>
                        <Button
                            onClick={handleUnlock}
                            disabled={paywallBusy || !paywallPrice}
                            className="brand-button"
                            data-testid="paywall-pay"
                        >
                            {paywallBusy ? "Redirecting…" : `Unlock for ${paywallPrice?.label || "—"}`}
                        </Button>
                    </DialogFooter>
                    <p className="text-[10px] text-muted-foreground mt-2 text-center">
                        Sample category and the community Ideas feed remain free, always.
                    </p>
                </DialogContent>
            </Dialog>
        </div>
    );
}
